import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const defaultForm = {
  enabled: false,
  botToken: '',
  botUsername: '',
  defaultChatId: '',
  defaultChatName: '',
  hiredAlertUserIds: [],
  hiredAlertChatIds: [],
  sendHiredAlertsToPrimaryChat: false,
  webhookUrl: '',
  webhookSecret: ''
};

const cardClassName = 'rounded-3xl border border-gray-200 bg-white shadow-sm';
const sectionClassName = 'rounded-2xl border border-gray-200 bg-white p-5 sm:p-6';
const inputClassName =
  'w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50';

const Toggle = ({ enabled, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
      enabled ? 'bg-blue-600' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
        enabled ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

const SectionHeader = ({ eyebrow, title, description }) => (
  <div className="space-y-1">
    {eyebrow ? <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">{eyebrow}</p> : null}
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    {description ? <p className="text-sm text-gray-500">{description}</p> : null}
  </div>
);

const Field = ({ label, hint, className = '', children }) => (
  <label className={`space-y-2 ${className}`}>
    <span className="text-sm font-medium text-gray-700">{label}</span>
    {children}
    {hint ? <span className="block text-xs leading-5 text-gray-500">{hint}</span> : null}
  </label>
);

const TelegramBotSettings = () => {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [testMessage, setTestMessage] = useState('Hiring CRM Telegram bot test message');
  const [testTargetUserId, setTestTargetUserId] = useState('');
  const [testTargetChatId, setTestTargetChatId] = useState('');
  const [users, setUsers] = useState([]);
  const [newAlertChatId, setNewAlertChatId] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [settingsResponse, usersResponse] = await Promise.all([
          axios.get('/api/settings/telegram-bot'),
          axios.get('/api/users')
        ]);
        if (settingsResponse.data) {
          setForm({ ...defaultForm, ...settingsResponse.data });
        }
        setUsers(usersResponse.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load Telegram bot settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setMessage('');
    setError('');
  };

  const handleUserToggle = (userId) => {
    setForm((prev) => ({
      ...prev,
      hiredAlertUserIds: prev.hiredAlertUserIds.includes(userId)
        ? prev.hiredAlertUserIds.filter((id) => id !== userId)
        : [...prev.hiredAlertUserIds, userId]
    }));
    setMessage('');
    setError('');
  };

  const handleAddAlertChatId = () => {
    const value = newAlertChatId.trim();
    if (!value) return;

    setForm((prev) => ({
      ...prev,
      hiredAlertChatIds: prev.hiredAlertChatIds.includes(value)
        ? prev.hiredAlertChatIds
        : [...prev.hiredAlertChatIds, value]
    }));
    setNewAlertChatId('');
    setMessage('');
    setError('');
  };

  const handleRemoveAlertChatId = (chatId) => {
    setForm((prev) => ({
      ...prev,
      hiredAlertChatIds: prev.hiredAlertChatIds.filter((item) => item !== chatId)
    }));
    setMessage('');
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    try {
      await axios.put('/api/settings/telegram-bot', form);
      setMessage('Telegram bot settings saved successfully.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save Telegram bot settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setMessage('');
    setError('');

    try {
      const response = await axios.post('/api/telegram/test-connection', form);
      setMessage(`Telegram bot connection successful: @${response.data.username || form.botUsername || 'bot'}`);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to test Telegram bot connection');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    setMessage('');
    setError('');

    try {
      const payload = {
        text: testMessage
      };

      if (testTargetUserId.trim()) {
        payload.userId = Number(testTargetUserId);
      } else if (testTargetChatId.trim()) {
        payload.chatId = testTargetChatId.trim();
      } else if (form.defaultChatId.trim()) {
        payload.chatId = form.defaultChatId.trim();
      }

      await axios.post('/api/telegram/test-send', payload);
      setMessage('Test Telegram message sent successfully.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to send Telegram test message');
    } finally {
      setSendingTest(false);
    }
  };

  const linkedUsersCount = useMemo(
    () => users.filter((user) => user.telegramUserId).length,
    [users]
  );

  const summaryItems = [
    {
      label: 'Bot status',
      value: form.enabled ? 'Enabled' : 'Disabled',
      tone: form.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-700'
    },
    {
      label: 'Primary destination',
      value: form.defaultChatName || form.defaultChatId || 'Not set',
      tone: 'bg-blue-50 text-blue-700'
    },
    {
      label: 'CRM users with Telegram ID',
      value: `${linkedUsersCount}/${users.length || 0}`,
      tone: 'bg-amber-50 text-amber-700'
    },
    {
      label: 'Extra alert chats',
      value: String(form.hiredAlertChatIds.length),
      tone: 'bg-violet-50 text-violet-700'
    }
  ];

  return (
    <Layout>
      <div className="space-y-6 px-4 py-6 sm:px-0">
        <div className={`${cardClassName} overflow-hidden`}>
          <div className="border-b border-blue-100 bg-gradient-to-r from-sky-50 via-white to-cyan-50 px-6 py-6 sm:px-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl space-y-3">
                <div className="inline-flex w-fit items-center rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 shadow-sm ring-1 ring-blue-100">
                  Telegram Setup
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Configure Telegram Bot</h1>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    Keep the same Telegram settings and tools, but make setup easier to scan: credentials first,
                    routing second, alerts third, then testing at the end.
                  </p>
                </div>
              </div>

              <div className="min-w-full rounded-2xl border border-white/80 bg-white/90 p-4 shadow-sm backdrop-blur lg:min-w-[320px]">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Bot availability</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Turn this on only after credentials and routing are ready.
                    </p>
                  </div>
                  <Toggle enabled={form.enabled} onToggle={() => handleChange('enabled', !form.enabled)} />
                </div>
              </div>
            </div>

            {!loading ? (
              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {summaryItems.map((item) => (
                  <div key={item.label} className="rounded-2xl border border-white/80 bg-white/80 px-4 py-4 shadow-sm">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{item.label}</p>
                    <div className="mt-3">
                      <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${item.tone}`}>
                        {item.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {loading ? (
            <div className="px-6 py-10 text-sm text-gray-500">Loading Telegram bot settings...</div>
          ) : (
            <div className="space-y-6 bg-gray-50/60 px-6 py-6 sm:px-8">
              <div className="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]">
                <div className="space-y-6">
                  <section className={sectionClassName}>
                    <SectionHeader
                      eyebrow="Core Setup"
                      title="Credentials and delivery defaults"
                      description="These fields define the shared bot identity and where it posts by default."
                    />

                    <div className="mt-5 grid gap-5 md:grid-cols-2">
                      <Field
                        label="Bot Token"
                        hint="Required. Create the bot with BotFather and paste the API token here."
                        className="md:col-span-2"
                      >
                        <input
                          type="password"
                          value={form.botToken}
                          onChange={(event) => handleChange('botToken', event.target.value)}
                          className={inputClassName}
                          placeholder="123456789:AA..."
                        />
                      </Field>

                      <Field
                        label="Bot Username"
                        hint="Optional, but useful for display and future deep links."
                      >
                        <input
                          value={form.botUsername}
                          onChange={(event) => handleChange('botUsername', event.target.value)}
                          className={inputClassName}
                          placeholder="hiringcrm_bot"
                        />
                      </Field>

                      <Field
                        label="Primary Chat ID"
                        hint="Optional. Use this for the primary group or admin chat where the bot should send updates."
                      >
                        <input
                          value={form.defaultChatId}
                          onChange={(event) => handleChange('defaultChatId', event.target.value)}
                          className={inputClassName}
                          placeholder="-1001234567890"
                        />
                      </Field>

                      <Field
                        label="Primary Chat Name"
                        hint="Optional label so admins know which group or channel this chat ID belongs to."
                      >
                        <input
                          value={form.defaultChatName}
                          onChange={(event) => handleChange('defaultChatName', event.target.value)}
                          className={inputClassName}
                          placeholder="Hiring Team Group"
                        />
                      </Field>

                      <Field
                        label="Webhook URL"
                        hint="Optional. Needed only if you plan to receive Telegram updates via webhook instead of polling."
                        className="md:col-span-2"
                      >
                        <input
                          value={form.webhookUrl}
                          onChange={(event) => handleChange('webhookUrl', event.target.value)}
                          className={inputClassName}
                          placeholder="https://your-domain.com/api/telegram/webhook"
                        />
                      </Field>

                      <Field
                        label="Webhook Secret"
                        hint="Optional. Adds an extra verification layer for incoming webhook requests."
                        className="md:col-span-2"
                      >
                        <input
                          type="password"
                          value={form.webhookSecret}
                          onChange={(event) => handleChange('webhookSecret', event.target.value)}
                          className={inputClassName}
                          placeholder="Optional secret token"
                        />
                      </Field>
                    </div>
                  </section>

                  <section className={sectionClassName}>
                    <SectionHeader
                      eyebrow="Alert Routing"
                      title="Hired alert recipients"
                      description="When a lead is marked as Hired, the bot sends the full candidate details to the people and chats selected here."
                    />

                    <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-4 text-sm leading-6 text-blue-900">
                      Group info belongs in shared Telegram bot settings, not on individual users. User rows should keep
                      personal Telegram IDs, while group or channel routing should use the primary chat fields above.
                    </div>

                    <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Send hired alerts to primary chat</p>
                          <p className="mt-1 text-xs text-gray-500">Uses the primary chat configured above.</p>
                        </div>
                        <Toggle
                          enabled={form.sendHiredAlertsToPrimaryChat}
                          onToggle={() =>
                            handleChange('sendHiredAlertsToPrimaryChat', !form.sendHiredAlertsToPrimaryChat)
                          }
                        />
                      </div>
                    </div>

                    <div className="mt-5 grid gap-5 lg:grid-cols-2">
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">CRM users to notify</p>
                          <p className="mt-1 text-xs text-gray-500">
                            Select the users who should receive hired alerts through their saved Telegram ID.
                          </p>
                        </div>
                        <div className="max-h-80 space-y-2 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-3">
                          {users.map((user) => (
                            <label
                              key={user.id}
                              className="flex items-start justify-between gap-3 rounded-xl border border-transparent bg-gray-50 px-3 py-3 transition hover:border-gray-200 hover:bg-white"
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                <div className="text-xs leading-5 text-gray-500">
                                  {user.email || 'No email'} •{' '}
                                  {user.telegramUserId ? `Telegram ID: ${user.telegramUserId}` : 'No Telegram ID'}
                                </div>
                              </div>
                              <input
                                type="checkbox"
                                checked={form.hiredAlertUserIds.includes(user.id)}
                                onChange={() => handleUserToggle(user.id)}
                                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Extra group or channel chat IDs</p>
                          <p className="mt-1 text-xs text-gray-500">
                            Add any additional Telegram groups or channels that should receive hired alerts.
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <input
                            value={newAlertChatId}
                            onChange={(event) => setNewAlertChatId(event.target.value)}
                            className={inputClassName}
                            placeholder="-1001234567890"
                          />
                          <button
                            type="button"
                            onClick={handleAddAlertChatId}
                            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                          >
                            Add
                          </button>
                        </div>

                        <div className="min-h-[110px] rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-4">
                          {form.hiredAlertChatIds.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {form.hiredAlertChatIds.map((chatId) => (
                                <button
                                  key={chatId}
                                  type="button"
                                  onClick={() => handleRemoveAlertChatId(chatId)}
                                  className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-100"
                                >
                                  {chatId} ×
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No extra chat IDs added yet.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>

                <div className="space-y-6">
                  <section className={sectionClassName}>
                    <SectionHeader
                      eyebrow="Quick Guide"
                      title="Recommended setup flow"
                      description="This keeps the page easier to use without changing any of the actual configuration options."
                    />

                    <div className="mt-5 space-y-3">
                      {[
                        'Add the bot token and optional bot username.',
                        'Set the primary chat ID and chat name if you want a shared destination.',
                        'Choose which users and extra chats should receive hired alerts.',
                        'Run connection and message tests before saving your final changes.'
                      ].map((step, index) => (
                        <div key={step} className="flex gap-3 rounded-2xl bg-gray-50 px-4 py-3">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                            {index + 1}
                          </div>
                          <p className="text-sm leading-6 text-gray-600">{step}</p>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className={sectionClassName}>
                    <SectionHeader
                      eyebrow="Testing"
                      title="Local testing"
                      description="Test the saved bot token first, then send a message either to a CRM user or directly to a chat ID."
                    />

                    <div className="mt-5 space-y-4">
                      <Field label="Test Message">
                        <textarea
                          value={testMessage}
                          onChange={(event) => setTestMessage(event.target.value)}
                          rows={4}
                          className={inputClassName}
                        />
                      </Field>

                      <Field
                        label="Target CRM User ID"
                        hint="If filled, the system uses that user's Telegram ID."
                      >
                        <input
                          value={testTargetUserId}
                          onChange={(event) => setTestTargetUserId(event.target.value)}
                          className={inputClassName}
                          placeholder="Uses that user's Telegram ID"
                        />
                      </Field>

                      <Field
                        label="Override Chat ID"
                        hint="If left empty, the test falls back to the primary chat ID."
                      >
                        <input
                          value={testTargetChatId}
                          onChange={(event) => setTestTargetChatId(event.target.value)}
                          className={inputClassName}
                          placeholder="Falls back to primary chat ID"
                        />
                      </Field>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={testingConnection}
                        className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {testingConnection ? 'Testing...' : 'Test Bot Connection'}
                      </button>
                      <button
                        type="button"
                        onClick={handleSendTest}
                        disabled={sendingTest}
                        className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {sendingTest ? 'Sending...' : 'Send Test Message'}
                      </button>
                    </div>
                  </section>
                </div>
              </div>

              {(message || error) && (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm ${
                    error
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-green-200 bg-green-50 text-green-700'
                  }`}
                >
                  {error || message}
                </div>
              )}

              <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-500">
                  All fields and behavior stay the same. This layout just separates setup, routing, and testing more clearly.
                </p>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TelegramBotSettings;
